import { createSelector, createSlice, type PayloadAction } from '@reduxjs/toolkit';
import { STORAGE_LIMITS } from '@src/constants/storage-limits';
import type { RootState } from '@src/store';
import { generateUniqueId } from '@src/utils/idGenerator';

/**
 * トースト通知の種類
 */
export type ToastSeverity = 'success' | 'error' | 'warning' | 'info';

/**
 * トースト通知のデータ型
 */
export interface Toast {
  id: string;
  message: string;
  severity: ToastSeverity;
  duration?: number;
  action?: {
    label: string;
    onClick: () => void;
  };
  persistent?: boolean;
  timestamp: number;
}

/**
 * トーストスライスの状態型
 */
interface ToastState {
  toasts: Toast[];
  maxToasts: number;
  defaultDuration: number;
}

/**
 * 初期状態
 */
const initialState: ToastState = {
  toasts: [],
  maxToasts: STORAGE_LIMITS.TOAST.MAX_VISIBLE,
  defaultDuration: STORAGE_LIMITS.TOAST.DEFAULT_DURATION,
};

/**
 * トースト通知管理スライス
 */
const toastSlice = createSlice({
  name: 'toast',
  initialState,
  reducers: {
    // トースト追加
    addToast: {
      reducer: (state, action: PayloadAction<Toast>) => {
        // 同一メッセージを持つ既存トーストを削除し、常に最新1件のみ保持
        state.toasts = state.toasts.filter((toast) => toast.message !== action.payload.message);

        // 最大数を超える場合は古いものから削除
        if (state.toasts.length >= state.maxToasts) {
          const nonPersistentToasts = state.toasts.filter((t) => !t.persistent);
          if (nonPersistentToasts.length > 0) {
            const oldestId = nonPersistentToasts[0].id;
            state.toasts = state.toasts.filter((t) => t.id !== oldestId);
          }
        }
        state.toasts.push(action.payload);
      },
      prepare: (params: Omit<Toast, 'id' | 'timestamp'>) => {
        return {
          payload: {
            ...params,
            id: generateUniqueId('toast'),
            timestamp: Date.now(),
          },
        };
      },
    },

    // トースト削除
    removeToast: (state, action: PayloadAction<string>) => {
      state.toasts = state.toasts.filter((toast) => toast.id !== action.payload);
    },

    // 複数トースト削除
    removeToasts: (state, action: PayloadAction<string[]>) => {
      state.toasts = state.toasts.filter((toast) => !action.payload.includes(toast.id));
    },

    // メッセージ単位でトースト削除
    removeToastsByMessage: (state, action: PayloadAction<string | string[]>) => {
      const messages = Array.isArray(action.payload) ? action.payload : [action.payload];
      state.toasts = state.toasts.filter((toast) => !messages.includes(toast.message));
    },

    // すべてのトーストをクリア
    clearAllToasts: (state) => {
      state.toasts = [];
    },

    // 永続的でないトーストをクリア
    clearNonPersistentToasts: (state) => {
      state.toasts = state.toasts.filter((toast) => toast.persistent);
    },

    // 設定更新
    updateToastSettings: (
      state,
      action: PayloadAction<{ maxToasts?: number; defaultDuration?: number }>
    ) => {
      if (action.payload.maxToasts !== undefined) {
        state.maxToasts = action.payload.maxToasts;
      }
      if (action.payload.defaultDuration !== undefined) {
        state.defaultDuration = action.payload.defaultDuration;
      }
    },
  },
});

// アクションのエクスポート
export const {
  addToast,
  removeToast,

  removeToastsByMessage,
} = toastSlice.actions;

// セレクター
export const selectToasts = (state: RootState) => state.toast.toasts;

// メモ化されたセレクター
const selectMaxToasts = (state: RootState) => state.toast.maxToasts;
const selectDefaultDuration = (state: RootState) => state.toast.defaultDuration;

export const selectToastSettings = createSelector(
  [selectMaxToasts, selectDefaultDuration],
  (maxToasts, defaultDuration) => ({
    maxToasts,
    defaultDuration,
  })
);

// ユーティリティアクション
export const showSuccessToast = (message: string, duration?: number) =>
  addToast({
    message,
    severity: 'success',
    duration,
  });

const _showErrorToast = (message: string, duration?: number) =>
  addToast({
    message,
    severity: 'error',
    duration,
  });

const _showWarningToast = (message: string, duration?: number) =>
  addToast({
    message,
    severity: 'warning',
    duration,
  });

const _showInfoToast = (message: string, duration?: number) =>
  addToast({
    message,
    severity: 'info',
    duration,
  });

export default toastSlice.reducer;
